import React, { useCallback, useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import FollowUpPreview from '../FollowUpPreview';
import { useAspSelector } from '../../../../../../test/jest-redux-hooks';
import useToggle from '../../../../../../hooks/useToggle';
import { createTextNode } from '../../../../api/sequence';
import AssigneePreview from '../AssigneePreview';

// notifications
import NotificationContext from '../../../../../../providers/NotificationProvider';
import { showErrorToast } from '../../../../../../components/commonComponents/gxAntdToast/toastUtils';

const OperationsPreview = ({
  node,
  color,
  isConnected,
  onEdgeMouseDown,
  allowToEditFlow,
  allowToShowEdge = true,
  onUpdateWaitTime,
  nodeCurrentStatus,
  onChange,
  closeNode,
}) => {
  const [isUpdating, setIsUpdating] = useToggle();
  const [removedEdges, setRemovedEdges] = useState([]);
  const { sequence_id, campaign_id } = useParams();
  const { currentBrand } = useAspSelector((state) => state.app);


  const notification = useContext(NotificationContext);
  
  // Normalize access to node.data
  const data = node?.data ?? {};
  
  // Determine operation type and data
  let operationType = data?.type?.toUpperCase();
  let hasOperations = false;
  let operations = [];

  // Handle different operation types
  if (operationType === 'FOLLOW_UP') {
    const derivedWaitTime = data?.operations?.delay ?? data?.follow_up?.delay;
    hasOperations = derivedWaitTime !== undefined;
    if (hasOperations) {
      operations.push({
        type: 'FOLLOW_UP',
        delay: derivedWaitTime,
      });
    }
  } else if (operationType === 'ASSIGNEE') {
    const assignee = data?.operations?.assignee || [];
    hasOperations = assignee.length > 0;
    if (hasOperations) {
      operations.push({
        type: 'ASSIGNEE',
        assignee: assignee,
      });
    }
  }

  if (!hasOperations) return null;

  const showSubmit = useCallback(() => {
    return true
  }, []);

  const updatedExistingNode = useCallback(
    async ({ payloadData, label, removedEdges: edgesToRemove = [] }) => {
      if (showSubmit() && allowToEditFlow) {
        // Combine any existing removed edges with new ones
        const allRemovedEdges = [...new Set([...removedEdges, ...edgesToRemove])];
        
        let payload = {
          brand_id: currentBrand?.brand_id,
          campaign_id: campaign_id,
          node_type: node?.type,
          label: label || node?.label,
          save: true,
          message_body: { ...payloadData },
        };

        // Only include removed_edges if there are any to remove
        if (allRemovedEdges.length > 0) {
          payload.deleted_edges = allRemovedEdges;
        }

        const sendNode = await createTextNode(
          sequence_id,
          node?.uniq_id,
          payload
        );

        setRemovedEdges([]);
        if (sendNode?.status === 200) {
          onChange({
            type: 'UPDATE_NODE_DATA',
            id: node?.uniq_id,
            data: sendNode?.data?.data,
          });

          // if (removeEdges?.length > 0) setRemovedEdges([]);

          const stateData = { selectedNode: null };
          onChange({
            type: 'UPDATE_DATA',
            data: stateData,
          });

          setIsUpdating(false);
          closeNode(node?.uniq_id);
        } else {
          setIsUpdating(false);
          showErrorToast(notification, 'Something went wrong');
        }
      }
    },
    [node, showSubmit]
  );

  const handleOk = useCallback(async (data, label, edgesToRemove = []) => {
    setIsUpdating(true);
    const payloadData = {
      ...data,
      type: node?.type?.toLowerCase(),
    };
    
    // If there are edges to remove, update the removedEdges state
    if (edgesToRemove.length > 0) {
      setRemovedEdges(prev => [...new Set([...prev, ...edgesToRemove])]);
    }
    
    await updatedExistingNode({ 
      payloadData, 
      label,
      removedEdges: edgesToRemove 
    });
  }, [node?.type, updatedExistingNode, setRemovedEdges]);

  const renderOperation = (operation, index) => {
    switch (operation.type) {
      case 'FOLLOW_UP': {
        const previewNode = {
          ...node,
          data: {
            ...data,
            delay: operation.delay,
            follow_up: {
              ...(data.follow_up || {}),
              delay: operation.delay,
            },
            operations: {
              ...(data.operations || {}),
              delay: operation.delay,
            },
          },
        };

        return (
          <FollowUpPreview
            key={index}
            node={previewNode}
            onUpdateWaitTime={(newDelay) => {
              if (onUpdateWaitTime) {
                onUpdateWaitTime({
                  operations: {
                    delay: newDelay,
                  },
                });
              }
            }}
            isUpdating={isUpdating}
            handleOk={handleOk}
            setRemovedEdges={setRemovedEdges}
            allowToEditFlow={allowToEditFlow}
          />
        );
      }
      case 'ASSIGNEE': {
        return (
          <AssigneePreview
            key={index}
            node={node}
            onUpdateAssignees={(updates) => {
              if (onUpdateWaitTime) {
                onUpdateWaitTime(updates);
              }
            }}
            isUpdating={isUpdating}
            handleOk={handleOk}
            setRemovedEdges={setRemovedEdges}
            allowToEditFlow={allowToEditFlow}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="w-full">
      {operations.map((operation, index) => renderOperation(operation, index))}
    </div>
  );
};

export default OperationsPreview;
